import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import MainLayout from "../../../component/atoms/layout/mainLayout";
import DetailPageLayout from "../../../component/atoms/layout/detail/detailPageLayout";
import PretendardText from "../../../component/atoms/text/pretendardText";
import DetailTop from "../../../component/atoms/layout/detail/detailTop";
import RedBtn from "../../../component/atoms/button/redBtn";
import BlueBtn from "../../../component/atoms/button/blueBtn";
import DetailPage from "../../../component/atoms/layout/detail/detailPage";
import TextArea from "../../../component/atoms/input/textarea";
import GrayBtn from "../../../component/atoms/button/grayBtn";
import GoBack from "../../../component/molecules/goBack";
import Header from "../../../component/organisms/header";
import Sidebar from "../../../component/organisms/sidebar";
import Modal from "../../../component/templates/Modal";
import scrollToTop from "../../../util/scrollToTop";
import { toastMsg } from "../../../util/toastMsg";
import { errorHandler } from "../../../util/errorHandler";
import BoardApi from "../../../api/board.api";
import moment from "moment";
import { formatNumberWithCommas } from "../../../util/formatNumber";
import ViewMore from "../../../component/molecules/viewMore";
import { route } from "../../../router/route";
import { fileDownload } from "../../../util/fileManagement";

const SecretArchiveDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const myID = sessionStorage.getItem("id");
  const [isWriteAuthority, setIsWriteAuthority] = useState(false);

  // ---------- 쓰기 권한체크 ---------- //
  useEffect(() => {
    const writeMenu = sessionStorage.getItem("writeMenu");

    if (writeMenu?.includes(route.secret_archive)) {
      setIsWriteAuthority(true);
    } else {
      setIsWriteAuthority(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 마운트가 되면 ---------- //
  useEffect(() => {
    getCommunityInfo();
    loadComments(1, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- 게시글 정보 가져오기 ---------- //
  const [communityInfo, setCommunityInfo] = useState({});

  const getCommunityInfo = async () => {
    try {
      const response = (await BoardApi.getByIdBoard(id)).data.data.content;

      setCommunityInfo(response);
    } catch (error) {
      errorHandler(error);
    }
  };

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const deleteHandler = async () => {
    try {
      await BoardApi.deleteBoard(id);
      toast(toastMsg.delete);
      setDeleteModalOpen(false);
      navigate(-1);
      scrollToTop();
    } catch (error) {
      errorHandler(error);
    }
  };

  // ---------- 댓글 관련 ---------- //
  const [commentList, setCommentList] = useState([]);
  const [commentTotalCount, setCommentTotalCount] = useState(0);
  const [commentPage, setCommentPage] = useState(1);
  const [commentCursor, setCommentCursor] = useState(0);
  const [commentInputValue, setCommentInputValue] = useState("");

  // 댓글 목록 불러오기
  const loadComments = async (page, cursor) => {
    // 새로운 페이지를 서버에서 불러옴
    const data = {
      page,
      size: 5,
      sort: "id",
      orderBy: "DESC",
      cursor,
      boardPostId: id,
    };

    try {
      const response = (await BoardApi.getListBoardComment(data)).data.data;

      if (commentTotalCount === 0) {
        const totalCount = response.totalCount;
        setCommentTotalCount(totalCount);
      }

      const newComments = await Promise.all(
        response.content.map(async (comment) => {
          return {
            ...comment,
            replyOpen: false,
            replyList: [], // 답글이 없을 경우 빈 배열
            replyPage: 1,
            replyCursor: 0,
            replyTotalCount: comment.replyCount,
            replyInputValue: "",
          };
        })
      );

      // 기존 댓글 목록에 새 댓글 추가
      setCommentList((prevList) => {
        // 중복된 ID를 가진 댓글 필터링
        const uniqueList = prevList.filter(
          (comment) =>
            !newComments.some((newComment) => newComment.id === comment.id)
        );
        return [...uniqueList, ...newComments];
      });
      setCommentPage(page + 1);
      setCommentCursor(newComments[newComments.length - 1].id);
    } catch (error) {
      console.error(error);
    }
  };

  // 댓글 작성
  const createComment = async () => {
    const data = {
      content: commentInputValue,
      boardPostId: id,
    };

    try {
      const createdId = (await BoardApi.createBoardComment(data)).data.data.id;

      const newComment = (await BoardApi.getByIdBoardComment(createdId)).data
        .data.content;

      newComment.replyOpen = false;
      newComment.replyList = [];
      newComment.replyInputValue = "";
      newComment.replyPage = 1;
      newComment.replyCursor = 0;
      newComment.replyTotalCount = 0;

      setCommentList((prevList) => [newComment, ...prevList]);

      setCommentTotalCount(commentTotalCount + 1);

      setCommentInputValue("");
    } catch (error) {
      console.error(error);
    }
  };

  // 댓글 삭제
  const [delCommentId, setDelCommentId] = useState(0);
  const [commentDeleteModalOpen, setCommentDeleteModalOpen] = useState(false);

  const deleteComment = async (commentId) => {
    try {
      await BoardApi.deleteBoardComment(commentId);

      // 삭제된 댓글을 제외한 목록으로 갱신
      setCommentList((prevList) =>
        prevList.filter((comment) => comment.id !== commentId)
      );

      setCommentTotalCount(commentTotalCount - 1);

      setCommentDeleteModalOpen(false);
      setDelCommentId(0);
      toast(toastMsg.delete);
    } catch (error) {
      console.error(error);
    }
  };

  // ---------- 답글 관련 ---------- //
  // 답글 목록 불러오기
  const loadReplies = async (boardCommentId, page, cursor) => {
    const data = {
      page,
      size: 5,
      sort: "id",
      orderBy: "ASC",
      cursor,
      boardCommentId,
    };

    try {
      const response = (await BoardApi.getListBoardReply(data)).data.data;

      return response;
    } catch (error) {
      console.error(error);
    }
  };

  // 답글 작성
  const createReply = async (boardCommentId, content) => {
    const data = {
      boardCommentId,
      content,
    };

    try {
      const createdId = (await BoardApi.createBoardReply(data)).data.data.id;

      const newReply = (await BoardApi.getByIdBoardReply(createdId)).data.data
        .content;

      return newReply;
    } catch (error) {
      console.error(error);
    }
  };

  // 답글 삭제
  const [delReplyId, setDelReplyId] = useState(0);
  const [replyDeleteModalOpen, setReplyDeleteModalOpen] = useState(false);

  const deleteReply = async (replyId) => {
    try {
      await BoardApi.deleteBoardReply(replyId);

      // 삭제된 답글을 제외한 목록으로 갱신
      setCommentList((prevList) =>
        prevList.map((comment) => {
          comment.replyList = comment.replyList.filter(
            (reply) => reply.id !== replyId
          );
          comment.replyTotalCount -= 1;
          return comment;
        })
      );

      setReplyDeleteModalOpen(false);
      setDelReplyId(0);
      toast(toastMsg.delete);
    } catch (error) {
      console.error(error);
    }
  };

  const [editId, setEditId] = useState(0);
  const [editValue, setEditValue] = useState("");

  const commentEditFunc = async () => {
    try {
      const data = {
        content: editValue,
      };

      await BoardApi.updateBoardComment(editId, data);

      const updatedPosts = commentList.map((post) =>
        post.id === editId ? { ...post, content: editValue } : post
      );

      setCommentList(updatedPosts);
      toast(toastMsg.update);
      setEditId(0);
      setEditValue("");
    } catch (error) {
      console.error(error);
    }
  };

  const replyEditFunc = async () => {
    try {
      const data = {
        content: editValue,
      };

      await BoardApi.updateBoardReply(editId, data);

      setCommentList((prevList) =>
        prevList.map((comment) => {
          comment.replyList = comment.replyList.map((post) =>
            post.id === editId ? { ...post, content: editValue } : post
          );

          return comment;
        })
      );

      toast(toastMsg.update);
      setEditId(0);
      setEditValue("");
    } catch (error) {
      console.error(error);
    }
  };

  const activationBoardChangeFunc = async (value) => {
    try {
      const data = {
        activation: value,
      };
      await BoardApi.updateActivationBoard(id, data);

      toast(toastMsg.update);
      getCommunityInfo();
    } catch (error) {
      errorHandler(error);
    }
  };

  const activationCommentChangeFunc = async (id, value) => {
    try {
      const data = {
        activation: value,
      };
      await BoardApi.updateActivationBoardComment(id, data);

      setCommentList((prevList) => {
        prevList = prevList.map((post) =>
          post.id === id ? { ...post, activation: value } : post
        );

        return prevList;
      });

      toast(toastMsg.update);
    } catch (error) {
      errorHandler(error);
    }
  };

  const activationReplyChangeFunc = async (id, value) => {
    try {
      const data = {
        activation: value,
      };
      await BoardApi.updateActivationBoardReply(id, data);

      toast(toastMsg.update);

      setCommentList((prevList) =>
        prevList.map((comment) => {
          comment.replyList = comment.replyList.map((post) =>
            post.id === id ? { ...post, activation: value } : post
          );

          return comment;
        })
      );
    } catch (error) {
      errorHandler(error);
    }
  };

  return (
    <>
      <Header />
      <MainLayout
        style={{
          position: "relative",
          gap: 306,
        }}
      >
        <Sidebar />

        {/* 본문 영역 */}
        <DetailPageLayout>
          {/* 게시물이 있는 경우 */}

          <DetailTop>
            <GoBack />
            {isWriteAuthority && (
              <div
                style={{
                  width: "fit-content",
                  display: "flex",
                  gap: 12,
                }}
              >
                <GrayBtn
                  onClick={() => {
                    if (communityInfo.activation) {
                      activationBoardChangeFunc(false);
                    } else {
                      activationBoardChangeFunc(true);
                    }
                  }}
                >
                  {communityInfo.activation
                    ? "게시글 숨기기"
                    : "게시글 숨김 풀기"}
                </GrayBtn>
                <BlueBtn
                  onClick={() => {
                    navigate(`${route.secret_archive_edit}/${id}`);
                    scrollToTop();
                  }}
                >
                  수정
                </BlueBtn>
                <RedBtn
                  onClick={() => {
                    setDeleteModalOpen(true);
                  }}
                >
                  삭제
                </RedBtn>
              </div>
            )}
          </DetailTop>
          <DetailPage>
            <ContentTitle>{communityInfo?.title || ""}</ContentTitle>
            <ContentSubTitleWrapper>
              <ContentSubTitle>
                {communityInfo?.member?.nickname || ""}
              </ContentSubTitle>
              <TextDivider />
              <ContentSubTitle>자료창고</ContentSubTitle>
              <TextDivider />
              <ContentSubTitle
                style={{
                  color: "rgba(181, 181, 181, 0.5)",
                }}
              >
                {moment(communityInfo?.createdAt).format("YYYY-MM-DD")}
              </ContentSubTitle>
              <TextDivider />
              <ContentSubTitle
                style={{
                  color: "rgba(181, 181, 181, 0.5)",
                }}
              >
                조회수{" "}
                {formatNumberWithCommas(communityInfo?.totalViewCount || 0)}
              </ContentSubTitle>
              <TextDivider />
              <ContentSubTitle
                style={{
                  color: "rgba(181, 181, 181, 0.5)",
                }}
              >
                댓글 {formatNumberWithCommas(communityInfo?.commentCount || 0)}
              </ContentSubTitle>
            </ContentSubTitleWrapper>

            <ContentDivider
              style={{
                marginBottom: 30,
              }}
            />

            {/* 본문 영역 */}
            <ContentParagraphWrapper>
              <ContentParagraph>
                {/* 에디터를 통해 작성된 내용 보여지는 코드 */}
                <div
                  style={{
                    width: "100%",
                    height: "fit-content",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: communityInfo?.content || "",
                  }}
                />
              </ContentParagraph>
            </ContentParagraphWrapper>

            <ContentDivider
              style={{
                marginBottom: 18,
              }}
            />

            {/* 첨부파일 영역 */}
            {communityInfo?.boardPostFileList?.length > 0 && (
              <>
                <ContentTitle>첨부파일</ContentTitle>
                <AttachmentFileList>
                  {communityInfo.boardPostFileList.map((file) => (
                    <AttachmentFileItem
                      key={file.id}
                      onClick={() =>
                        fileDownload(
                          file.fileServerFileName,
                          file.fileOriginFileName
                        )
                      }
                    >
                      <img
                        src="/assets/admin/icons/ico_file_file.svg"
                        alt=""
                        style={{ width: 20, height: 20, flexShrink: 0 }}
                      />
                      <PretendardText
                        style={{
                          fontSize: 14,
                          color: "#aaa",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.fileOriginFileName}
                      </PretendardText>
                      <img
                        src="/assets/admin/icons/ico_file_download.svg"
                        alt="다운로드"
                        style={{ width: 20, height: 20, flexShrink: 0 }}
                      />
                    </AttachmentFileItem>
                  ))}
                </AttachmentFileList>
                <ContentDivider
                  style={{
                    marginBottom: 18,
                  }}
                />
              </>
            )}

            {/* 댓글 영역 */}
            <ContentTitle>
              댓글 {formatNumberWithCommas(commentTotalCount)}
            </ContentTitle>
            <div
              style={{
                width: "100%",
                position: "relative",
                height: "fit-content",
              }}
            >
              <TextArea
                type={"text"}
                placeholder={"내용을 입력해주세요"}
                value={commentInputValue}
                onChange={(e) => {
                  setCommentInputValue(e.target.value);
                }}
                style={{
                  width: "1044px",
                  minHeight: "104px",
                }}
              />
              <GrayBtn
                style={{
                  backgroundColor: "#313233",
                  position: "absolute",
                  right: 15,
                  bottom: 15,
                }}
                onClick={() => {
                  if (commentInputValue.trim() === "") {
                    toast("댓글 내용을 입력해주세요.");
                    return;
                  }

                  createComment();
                }}
              >
                등록
              </GrayBtn>
            </div>

            <CommentContainer>
              {commentList.map((item, index) => {
                return (
                  <React.Fragment key={index}>
                    <CommentWrapper>
                      <div
                        style={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <CommentUserInfoWrapper>
                          <CommentUserInfoText>
                            {item?.member?.nickname || ""}
                          </CommentUserInfoText>
                          <TextDivider />
                          <CommentUserInfoText
                            style={{
                              color: "rgba(181, 181, 181, 0.5)",
                            }}
                          >
                            {moment(item.createdAt).format("YYYY-MM-DD")}
                          </CommentUserInfoText>
                        </CommentUserInfoWrapper>
                        <CommentParagraphWrapper>
                          {editId === item.id ? (
                            <TextArea
                              type={"text"}
                              placeholder={"내용을 입력해주세요"}
                              value={editValue}
                              onChange={(e) => {
                                setEditValue(e.target.value);
                              }}
                              style={{
                                width: "1044px",
                                minHeight: "104px",
                              }}
                            />
                          ) : (
                            <CommentParagraph>{item.content}</CommentParagraph>
                          )}
                        </CommentParagraphWrapper>
                        <CommentActiveAreaWrapper>
                          <CommentReplyText
                            onClick={() => {
                              item.replyOpen = !item.replyOpen;
                              setCommentList([...commentList]);

                              if (item.replyOpen) {
                                loadReplies(item.id, 1, 0).then((response) => {
                                  const replies = response.content;
                                  item.replyList = replies;
                                  item.replyPage = item.replyPage + 1;
                                  item.replyCursor = replies[replies.length - 1]
                                    ?.id
                                    ? replies[replies.length - 1].id
                                    : 0;
                                  setCommentList([...commentList]);
                                });
                              }
                            }}
                          >
                            {item?.replyOpen
                              ? "답글접기"
                              : item.replyTotalCount > 0
                                ? `답글 ${item.replyTotalCount}개`
                                : "답글쓰기"}
                          </CommentReplyText>
                          {editId === item.id ? (
                            <CommentActiveBtnWrapper>
                              <BlueBtn
                                onClick={() => {
                                  commentEditFunc();
                                }}
                              >
                                수정
                              </BlueBtn>
                              <RedBtn
                                onClick={() => {
                                  setEditId(0);
                                  setEditValue("");
                                }}
                              >
                                취소
                              </RedBtn>
                            </CommentActiveBtnWrapper>
                          ) : (
                            <CommentActiveBtnWrapper>
                              {Number(myID) === item.member.id ? (
                                <>
                                  <BlueBtn
                                    onClick={() => {
                                      setEditId(item.id);
                                      setEditValue(item.content);
                                    }}
                                  >
                                    수정
                                  </BlueBtn>
                                  <RedBtn
                                    onClick={() => {
                                      setDelCommentId(item.id);
                                      setCommentDeleteModalOpen(true);
                                    }}
                                  >
                                    삭제
                                  </RedBtn>
                                </>
                              ) : (
                                isWriteAuthority && (
                                  <>
                                    <GrayBtn
                                      onClick={() => {
                                        if (item.activation) {
                                          activationCommentChangeFunc(
                                            item.id,
                                            false
                                          );
                                        } else {
                                          activationCommentChangeFunc(
                                            item.id,
                                            true
                                          );
                                        }
                                      }}
                                    >
                                      {item.activation ? "숨기기" : "숨김 풀기"}
                                    </GrayBtn>
                                    <RedBtn
                                      onClick={() => {
                                        setDelCommentId(item.id);
                                        setCommentDeleteModalOpen(true);
                                      }}
                                    >
                                      삭제
                                    </RedBtn>
                                  </>
                                )
                              )}
                            </CommentActiveBtnWrapper>
                          )}
                        </CommentActiveAreaWrapper>
                      </div>
                    </CommentWrapper>
                    {item?.replyOpen && (
                      <div
                        style={{
                          width: "100%",
                          height: "fit-content",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          padding: "15px 20px 15px 20px",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            position: "relative",
                            height: "fit-content",
                          }}
                        >
                          <TextArea
                            type={"text"}
                            placeholder={"내용을 입력해주세요"}
                            value={item.replyInputValue}
                            onChange={(e) => {
                              item.replyInputValue = e.target.value;
                              setCommentList([...commentList]);
                            }}
                            style={{
                              width: "100%",
                              minHeight: "104px",
                            }}
                          />
                          <GrayBtn
                            style={{
                              backgroundColor: "#313233",
                              position: "absolute",
                              right: 15,
                              bottom: 15,
                            }}
                            onClick={() => {
                              if (item.replyInputValue.trim() === "") {
                                toast("답글 내용을 입력해주세요.");
                                return;
                              }

                              createReply(item.id, item.replyInputValue).then(
                                (newReply) => {
                                  item.replyList = [
                                    ...item.replyList,
                                    newReply,
                                  ];
                                  item.replyTotalCount += 1;
                                  item.replyInputValue = "";
                                  setCommentList([...commentList]);
                                  toast("등록되었습니다.");
                                }
                              );
                            }}
                          >
                            등록
                          </GrayBtn>
                        </div>
                        {item?.replyList.map((reply, index) => {
                          return (
                            <CommentWrapper key={index}>
                              <CommentReplyImg
                                src="/assets/admin/icons/ico_reply.svg"
                                alt="답글"
                              />
                              <div
                                style={{
                                  width: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                }}
                              >
                                <CommentUserInfoWrapper>
                                  <CommentUserInfoText>
                                    {reply?.member?.nickname || ""}
                                  </CommentUserInfoText>
                                  <TextDivider />
                                  <CommentUserInfoText
                                    style={{
                                      color: "rgba(181, 181, 181, 0.5)",
                                    }}
                                  >
                                    {moment(reply.createdAt).format(
                                      "YYYY-MM-DD"
                                    )}
                                  </CommentUserInfoText>
                                </CommentUserInfoWrapper>
                                <CommentParagraphWrapper>
                                  {editId === reply.id ? (
                                    <TextArea
                                      type={"text"}
                                      placeholder={"내용을 입력해주세요"}
                                      value={editValue}
                                      onChange={(e) => {
                                        setEditValue(e.target.value);
                                      }}
                                      style={{
                                        width: "970px",
                                        minHeight: "104px",
                                      }}
                                    />
                                  ) : (
                                    <CommentParagraph>
                                      {reply.content}
                                    </CommentParagraph>
                                  )}
                                </CommentParagraphWrapper>
                                <CommentActiveAreaWrapper>
                                  {editId === reply.id ? (
                                    <CommentActiveBtnWrapper
                                      style={{
                                        width: "100%",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      <BlueBtn
                                        onClick={() => {
                                          replyEditFunc();
                                        }}
                                      >
                                        수정
                                      </BlueBtn>
                                      <RedBtn
                                        onClick={() => {
                                          setEditId(0);
                                          setEditValue("");
                                        }}
                                      >
                                        취소
                                      </RedBtn>
                                    </CommentActiveBtnWrapper>
                                  ) : (
                                    <CommentActiveBtnWrapper
                                      style={{
                                        width: "100%",
                                        justifyContent: "flex-end",
                                      }}
                                    >
                                      {Number(myID) === reply?.member?.id ? (
                                        <>
                                          <BlueBtn
                                            onClick={() => {
                                              setEditId(reply.id);
                                              setEditValue(reply.content);
                                            }}
                                          >
                                            수정
                                          </BlueBtn>
                                          <RedBtn
                                            onClick={() => {
                                              setDelReplyId(reply.id);
                                              setReplyDeleteModalOpen(true);
                                            }}
                                          >
                                            삭제
                                          </RedBtn>
                                        </>
                                      ) : (
                                        isWriteAuthority && (
                                          <>
                                            <GrayBtn
                                              onClick={() => {
                                                if (reply.activation) {
                                                  activationReplyChangeFunc(
                                                    reply.id,
                                                    false
                                                  );
                                                } else {
                                                  activationReplyChangeFunc(
                                                    reply.id,
                                                    true
                                                  );
                                                }
                                              }}
                                            >
                                              {reply.activation
                                                ? "숨기기"
                                                : "숨김 풀기"}
                                            </GrayBtn>
                                            <RedBtn
                                              onClick={() => {
                                                setDelReplyId(reply.id);
                                                setReplyDeleteModalOpen(true);
                                              }}
                                            >
                                              삭제
                                            </RedBtn>
                                          </>
                                        )
                                      )}
                                    </CommentActiveBtnWrapper>
                                  )}
                                </CommentActiveAreaWrapper>
                              </div>
                            </CommentWrapper>
                          );
                        })}
                        {item.replyList.length < item.replyTotalCount && (
                          <ViewMore
                            onClick={() => {
                              loadReplies(
                                item.id,
                                item.replyPage,
                                item.replyCursor
                              ).then((response) => {
                                const replies = response.content;
                                item.replyList = [
                                  ...item.replyList,
                                  ...replies,
                                ];
                                item.replyPage = item.replyPage + 1;
                                item.replyCursor =
                                  replies[replies.length - 1].id;
                                setCommentList([...commentList]);
                              });
                            }}
                          />
                        )}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}

              {commentList.length < commentTotalCount && (
                <ViewMore
                  onClick={() => {
                    loadComments(commentPage, commentCursor);
                  }}
                />
              )}
            </CommentContainer>
          </DetailPage>
        </DetailPageLayout>
      </MainLayout>

      {deleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setDeleteModalOpen}
          onClick={deleteHandler}
        />
      )}

      {commentDeleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setCommentDeleteModalOpen}
          onClick={() => {
            deleteComment(delCommentId);
          }}
        />
      )}

      {replyDeleteModalOpen && (
        <Modal
          type="negative"
          title="삭제하시겠습니까?"
          text={
            <>
              삭제된 데이터는 복구가 불가합니다.
              <br />
              그래도 삭제하시겠습니까?
            </>
          }
          setModalOpen={setReplyDeleteModalOpen}
          onClick={() => {
            deleteReply(delReplyId);
          }}
        />
      )}
    </>
  );
};

export default SecretArchiveDetail;

const ContentTitle = styled(PretendardText).attrs((props) => {})`
  font-size: 18px;
  font-weight: 600;
  line-height: 32.5px;
  color: #f3f3f3;
  margin: 14px 0px 10px 0px;
`;

const ContentSubTitleWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
`;

const ContentSubTitle = styled(PretendardText).attrs((props) => {})`
  font-size: 14px;
  font-weight: 400;
  line-height: 19.6px;
  color: #aaa;
`;

const TextDivider = styled.span.attrs((props) => {})`
  width: 1px;
  height: 19px;
  background-color: rgba(255, 255, 255, 0.12);
`;

const ContentDivider = styled.div.attrs((props) => {})`
  width: 100%;
  height: 1px;
  background-color: rgba(255, 255, 255, 0.12);
`;

const ContentParagraphWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: "fit-content";
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 100px;
`;

const ContentParagraph = styled(PretendardText).attrs((props) => {})`
  font-weight: 400;
  font-size: 15px;
  line-height: 21.5px;
  color: #aaa;
`;

const CommentContainer = styled.div.attrs((props) => {})`
  width: 100%;
  height: "fit-content";
  display: flex;
  flex-direction: column;
  margin-bottom: 115px;
`;

const CommentWrapper = styled.div.attrs((props) => {})`
  width: 100%;
  height: "fit-content";
  display: flex;
  gap: 12.5px;
  padding: 19px 0px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
`;

const CommentUserInfoWrapper = styled(ContentSubTitleWrapper).attrs(
  (props) => {}
)`
  margin-bottom: 10px;
`;

const CommentUserInfoText = styled(ContentSubTitle).attrs((props) => {})``;

const CommentParagraphWrapper = styled(ContentParagraphWrapper).attrs(
  (props) => {}
)`
  margin-bottom: 16px;
`;

const CommentParagraph = styled(ContentParagraph).attrs((props) => {})``;

const CommentActiveAreaWrapper = styled.div.attrs((props) => {})`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CommentReplyText = styled(PretendardText).attrs((props) => {})`
  font-weight: 600;
  font-size: 15px;
  line-height: 21.5px;
  color: rgba(181, 181, 181, 0.5);
  cursor: pointer;
`;

const CommentActiveBtnWrapper = styled.div.attrs((props) => {})`
  width: fit-content;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const CommentReplyImg = styled.img.attrs((props) => {})`
  width: 16px;
  height: 16px;
`;

const AttachmentFileList = styled.ul`
  width: 100%;
  margin: 12px 0;
  padding: 0;
  list-style: none;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  overflow: hidden;
`;

const AttachmentFileItem = styled.li`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
  cursor: pointer;
  transition: background 0.2s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
  }
`;
